/**
 * Authorization API schemas
 */

import { z } from 'zod';
import type { AuthorizableAction, ResourceType } from '@pillar-portal/database/dist/frontend';
import {
    ALL_AUTHORIZABLE_ACTIONS,
    ALL_RESOURCE_TYPES,
} from '@pillar-portal/database/dist/frontend';
import { NonEmptyStringSchema, PositiveIntSchema } from './common.schemas';
import { AUTHORIZATION_MAX_PAGE_LIMIT, type IsAuthorizedRequest } from '../authorization.types';
import { ResourceDesignatorSchema } from '../permission.types';
export * from '../authorization.types';

/**
 * Authorization request primitives
 */
export const AuthorizableActionSchema: z.ZodType<AuthorizableAction> = z.enum(
    ALL_AUTHORIZABLE_ACTIONS
);

export const ResourceTypeSchema: z.ZodType<ResourceType> = z.enum(ALL_RESOURCE_TYPES);

/**
 * Query param schemas
 */
export const OrganizationIdQuerySchema: z.ZodType<number> = PositiveIntSchema;
export const UserIdQuerySchema: z.ZodType<number> = PositiveIntSchema;
export const RoleAssignmentIdQuerySchema: z.ZodType<number> = PositiveIntSchema;
export const ActionQuerySchema: z.ZodType<AuthorizableAction> = AuthorizableActionSchema;
export const ResourceTypeQuerySchema: z.ZodType<ResourceType> = ResourceTypeSchema;
export const ResourceIdQuerySchema: z.ZodType<string> = NonEmptyStringSchema;

/**
 * Pagination query param schemas for authorization list endpoints.
 *
 * - `limit` is optional; backend applies a default if omitted.
 * - `cursor` is optional; when present it must be a positive integer cursor.
 */
export const AuthorizationPaginationLimitQuerySchema: z.ZodType<number | undefined> = z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .transform((value: number | undefined) => {
        if (value === undefined) {
            return undefined;
        }
        return Math.min(value, AUTHORIZATION_MAX_PAGE_LIMIT);
    });

export const AuthorizationPaginationCursorQuerySchema: z.ZodType<number | undefined> =
    PositiveIntSchema.optional();

export const AuthorizationOpaqueCursorQuerySchema: z.ZodType<string | undefined> = z
    .string()
    .trim()
    .min(1)
    .optional();

export const OrganizationUuidQuerySchema: z.ZodType<string> = z.string().uuid();
export const UserUuidQuerySchema: z.ZodType<string> = z.string().uuid();
export const RoleAssignmentUuidQuerySchema: z.ZodType<string> = z.string().uuid();

export const AccessibleOrganizationIdsQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationPaginationCursorQuerySchema,
});

export type AccessibleOrganizationIdsQuery = z.infer<typeof AccessibleOrganizationIdsQuerySchema>;

export const AccessibleOrganizationIdsOpaqueQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
});

export type AccessibleOrganizationIdsOpaqueQuery = z.infer<
    typeof AccessibleOrganizationIdsOpaqueQuerySchema
>;

export const AccessibleUserIdsQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationPaginationCursorQuerySchema,
    organization_id: OrganizationIdQuerySchema.optional(),
});

export type AccessibleUserIdsQuery = z.infer<typeof AccessibleUserIdsQuerySchema>;

export const AccessibleUserIdsOpaqueQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
    organization_uuid: OrganizationUuidQuerySchema.optional(),
});

export type AccessibleUserIdsOpaqueQuery = z.infer<typeof AccessibleUserIdsOpaqueQuerySchema>;

export const IsAuthorizedRequestSchema: z.ZodType<IsAuthorizedRequest> = z.object({
    action: AuthorizableActionSchema,
    resource_type: ResourceTypeSchema,
    target_resource: ResourceDesignatorSchema,
});
